import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { NewMessageComposerProvider } from './context/NewMessageComposerContext';
import NewMessageComposer from './components/MessagePage/NewMessageComposer';
import UnifiedAuthPage from './components/UnifiedAuthPage';
import OAuthCallbackPage from './components/OAuthCallbackPage';
import ProfilePage from './components/ProfilePage';
import UserProfilePage from './components/ProfilePage/UserProfilePage';
import HomePage from './components/HomePage';
import SettingsPage from './components/SettingsPage';
import WalletPage from './components/WalletPage';
import SubscriptionsPage from './components/SubscriptionsPage';
import SupportPage from './components/SupportPage';
import ReportHistoryPage from './components/ReportHistoryPage';
import MessagePage from './components/MessagePage';
import GoLivePage from './components/GoLivePage';
import WatchPage from './components/WatchPage/WatchPage';
import ViewerCamOverlay from './components/GoLivePage/ViewerCamOverlay';
import LegalPage from './components/LegalPage';
import UploadPage from './components/UploadPage';
import DownloadPage from './components/DownloadPage/DownloadPage';
import AdminDashboard, {
  OverviewPage as AdminOverviewPage,
  RevenuePage as AdminRevenuePage,
  UsersPage as AdminUsersPage,
  ModerationPage as AdminModerationPage,
  SupportTicketsPage as AdminSupportTicketsPage,
  ComingSoonPage as AdminComingSoonPage,
} from './components/AdminDashboard';
import CommunityPage from './components/CommunityPage';
import { ROUTES } from './constants';
import {
  authApi,
  clearStoredAuth,
  readStoredToken,
  writeStoredUser,
} from './services/authApi';
import { resolvePostAuthRoute } from './utils/postAuthRoute';

const BUILD_CHECK_THROTTLE_MS = 15000;
const BUILD_RELOAD_TARGET_KEY = 'polarace_build_reload_target';

function normalizeBuildAssetPath(value) {
  if (!value) {
    return null;
  }

  try {
    return new URL(value, window.location.origin).pathname;
  } catch {
    return value;
  }
}

function getCurrentMainScriptPath() {
  const currentScript = Array.from(document.scripts || []).find((script) =>
    /\/static\/js\/main\.[^/]+\.js$/.test(script.src || '')
  );

  return currentScript ? normalizeBuildAssetPath(currentScript.src) : null;
}

function AuthEntryRoute() {
  const navigate = useNavigate();
  const location = useLocation();
  const hasPendingAuthStep = Boolean(location.state?.step);
  const token = hasPendingAuthStep ? null : readStoredToken();
  const [forceShowAuth, setForceShowAuth] = useState(false);

  useEffect(() => {
    if (!token || hasPendingAuthStep) {
      return undefined;
    }

    let cancelled = false;

    const restoreSession = async () => {
      try {
        const response = await authApi.getCurrentUser(token);
        if (response?.user) {
          writeStoredUser(response.user);
        }

        const nextRoute = await resolvePostAuthRoute();
        if (!cancelled) {
          navigate(nextRoute, { replace: true });
        }
      } catch (error) {
        if (cancelled) {
          return;
        }

        if (error?.status === 401) {
          clearStoredAuth();
          setForceShowAuth(true);
          return;
        }

        const nextRoute = await resolvePostAuthRoute();
        if (!cancelled) {
          navigate(nextRoute, { replace: true });
        }
      }
    };

    restoreSession();

    return () => {
      cancelled = true;
    };
  }, [hasPendingAuthStep, navigate, token]);

  if (forceShowAuth || hasPendingAuthStep || !token) {
    return <UnifiedAuthPage />;
  }

  return null;
}

function RequireAuthRoute({ children }) {
  const token = readStoredToken();

  if (!token) {
    return <Navigate to={ROUTES.WELCOME} replace />;
  }

  return children;
}

function BuildRefreshGuard() {
  const location = useLocation();
  const checkInFlightRef = useRef(false);
  const lastCheckAtRef = useRef(0);
  const checkForNewBuildRef = useRef(async () => {});

  checkForNewBuildRef.current = async (force = false) => {
    if (checkInFlightRef.current) {
      return;
    }

    const now = Date.now();
    if (!force && now - lastCheckAtRef.current < BUILD_CHECK_THROTTLE_MS) {
      return;
    }

    lastCheckAtRef.current = now;
    checkInFlightRef.current = true;

    try {
      const response = await fetch(`/asset-manifest.json?ts=${now}`, { cache: 'no-store' });
      if (!response.ok) {
        return;
      }

      const manifest = await response.json();
      const nextMainScriptPath = normalizeBuildAssetPath(manifest?.files?.['main.js']);
      const currentMainScriptPath = getCurrentMainScriptPath();

      if (!nextMainScriptPath || !currentMainScriptPath) {
        return;
      }

      if (nextMainScriptPath === currentMainScriptPath) {
        window.sessionStorage.removeItem(BUILD_RELOAD_TARGET_KEY);
        return;
      }

      if (window.sessionStorage.getItem(BUILD_RELOAD_TARGET_KEY) === nextMainScriptPath) {
        return;
      }

      window.sessionStorage.setItem(BUILD_RELOAD_TARGET_KEY, nextMainScriptPath);
      window.location.reload();
    } catch {
      // Ignore version-check failures and keep the current session alive.
    } finally {
      checkInFlightRef.current = false;
    }
  };

  useEffect(() => {
    checkForNewBuildRef.current();
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    const handleFocus = () => {
      checkForNewBuildRef.current(true);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkForNewBuildRef.current(true);
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return null;
}

/**
 * Root application component with routing configuration.
 *
 * Route hierarchy:
 * - / → UnifiedAuthPage (landing + auth, first page users see)
 * - /auth → redirect to / for bookmarked auth links
 * - /auth/callback → OAuthCallbackPage (handles backend OAuth redirect)
 * - /login, /signup, /forgot-password → Redirect to /
 * - /home → Home Dashboard (main experience after login)
 * - /profile → User profile setup
 * - /settings → Account settings
 * - /wallet → Transaction history
 * - /support → Help and support form
 * - /reports → Report history
 * - /messages → Messaging interface
 *
 * @returns {JSX.Element}
 */
function App() {
  return (
    <BrowserRouter>
      <NewMessageComposerProvider>
      <BuildRefreshGuard />
      <NewMessageComposer />
      <Routes>
        {/* Landing + Auth — first page users see */}
        <Route path={ROUTES.WELCOME} element={<AuthEntryRoute />} />

        {/* /auth redirects to / for any bookmarked links */}
        <Route path={ROUTES.AUTH} element={<Navigate to={ROUTES.WELCOME} replace />} />

        {/* OAuth Callback — handles redirect from backend after OAuth */}
        <Route path={ROUTES.AUTH_CALLBACK} element={<OAuthCallbackPage />} />

        {/* Legacy redirects */}
        <Route path={ROUTES.LOGIN} element={<Navigate to={ROUTES.WELCOME} replace />} />
        <Route path={ROUTES.SIGNUP} element={<Navigate to={ROUTES.WELCOME} replace />} />
        <Route path={ROUTES.FORGOT_PASSWORD} element={<Navigate to={ROUTES.WELCOME} replace />} />

        {/* Home Dashboard — main experience after login */}
        <Route path={ROUTES.HOME} element={<RequireAuthRoute><HomePage /></RequireAuthRoute>} />

        {/* Go Live — streaming dashboard */}
        <Route path={ROUTES.GO_LIVE} element={<RequireAuthRoute><GoLivePage /></RequireAuthRoute>} />

        {/* Upload — media library and ad asset uploads */}
        <Route path={ROUTES.UPLOAD} element={<RequireAuthRoute><UploadPage /></RequireAuthRoute>} />

        {/* Download — OBS extension page */}
        <Route path={ROUTES.DOWNLOAD} element={<DownloadPage />} />

        {/* Admin — nested dashboard */}
        <Route path={ROUTES.ADMIN} element={<AdminDashboard />}>
          <Route index element={<AdminOverviewPage />} />
          <Route path="revenue" element={<AdminRevenuePage />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="moderation" element={<AdminModerationPage />} />
          <Route path="support" element={<AdminSupportTicketsPage />} />
          <Route
            path="streams"
            element={<AdminComingSoonPage title="Streams" description="Live stream activity and historical analytics." />}
          />
          <Route
            path="payouts"
            element={<AdminComingSoonPage title="Payouts" description="Streamer earnings and payout queue." />}
          />
          <Route
            path="reports"
            element={<AdminComingSoonPage title="Reports" description="User-submitted reports of other users or content." />}
          />
          <Route
            path="settings"
            element={<AdminComingSoonPage title="Settings" description="Platform configuration: fee %, spade rates, feature flags." />}
          />
        </Route>

        {/* Profile setup (accessible from user dropdown) */}
        <Route path={ROUTES.PROFILE} element={<RequireAuthRoute><ProfilePage /></RequireAuthRoute>} />
        <Route path={ROUTES.PUBLIC_PROFILE} element={<UserProfilePage />} />
        <Route path={ROUTES.WATCH} element={<WatchPage />} />
        <Route path={ROUTES.VIEWER_CAM_OVERLAY} element={<ViewerCamOverlay />} />

        {/* Account settings */}
        <Route path={ROUTES.SETTINGS} element={<RequireAuthRoute><SettingsPage /></RequireAuthRoute>} />

        {/* Wallet — transaction history */}
        <Route path={ROUTES.WALLET} element={<RequireAuthRoute><WalletPage /></RequireAuthRoute>} />

        {/* Subscriptions — viewer→streamer 30-day subscriptions */}
        <Route path={ROUTES.SUBSCRIPTIONS} element={<RequireAuthRoute><SubscriptionsPage /></RequireAuthRoute>} />

        {/* Support — help form */}
        <Route path={ROUTES.SUPPORT} element={<RequireAuthRoute><SupportPage /></RequireAuthRoute>} />
        <Route path={ROUTES.CONTACT} element={<RequireAuthRoute><SupportPage /></RequireAuthRoute>} />

        {/* Report History */}
        <Route path={ROUTES.REPORTS} element={<RequireAuthRoute><ReportHistoryPage /></RequireAuthRoute>} />

        {/* Legal pages */}
        <Route path={ROUTES.TERMS} element={<LegalPage />} />
        <Route path={ROUTES.PRIVACY} element={<LegalPage />} />
        <Route path={ROUTES.GUIDELINES} element={<LegalPage />} />

        {/* Messages */}
        <Route path={ROUTES.MESSAGES} element={<RequireAuthRoute><MessagePage /></RequireAuthRoute>} />

        {/* Community — IVS Chat */}
        <Route path={ROUTES.COMMUNITY} element={<RequireAuthRoute><CommunityPage /></RequireAuthRoute>} />
        <Route
          path={ROUTES.COMMUNITY_USER}
          element={
            String(process.env.REACT_APP_ENABLE_PER_USER_CHATS).toLowerCase() === 'true'
              ? <RequireAuthRoute><CommunityPage /></RequireAuthRoute>
              : <Navigate to={ROUTES.COMMUNITY} replace />
          }
        />

        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to={ROUTES.WELCOME} replace />} />
      </Routes>
      </NewMessageComposerProvider>
    </BrowserRouter>
  );
}

export default App;
